import React from "react";
import { TableHeaderItem } from "../../../hooks/useTable";
import styled, { keyframes } from "styled-components";
import { TableSortIcon } from "../../atoms/icons/TableSortIcon";
import { DeleteIcon } from "../../atoms/icons/DeleteIcon";
import { Checkbox } from "../../atoms/Checkbox";
import { COLOR_GRAY_BACKGROUND, COLOR_TABLE_TITLE } from "../../../constants/color";

type Props<DataType> = {
  headers: TableHeaderItem<DataType>[];
  selectable?: boolean;
  editable?: boolean;
  deletable?: boolean;
  isChecked?: boolean;
  isSomeSelected?: boolean;
  disableDeleteButton?: boolean;
  onSelectAll?: (checked: boolean) => void;
  columnWidths?: string[]; // New prop for custom column widths
};

export const TableHeader = <DataType extends {}>({
  headers,
  selectable,
  editable,
  deletable,
  isChecked,
  isSomeSelected,
  disableDeleteButton,
  onSelectAll,
  columnWidths,
}: Props<DataType>) => {
  return (
    <StyledTableHead>
      <tr>
        {selectable && (
          <th style={{ width: "24px" }}>
            <Checkbox
              name="table-all"
              variant="empty"
              onChange={onSelectAll}
              checked={isChecked}
              isSomeSelected={isSomeSelected}
            />
          </th>
        )}

        {headers.map((header, i) => {
          const isLastColumn = i === headers.length - 1;

          return (
            <StyledTableHeaderContainer
              key={`table-header-${i}`}
              $width={columnWidths?.[i]}
            >
              <StyledTableHeadItem
                $isClickable={header.sortKey !== undefined}
                $isDisabled={header.disabled}
                onClick={header.disabled ? undefined : header.onClick}
                style={{
                  display: "flex",
                  justifyContent: isLastColumn ? "space-between" : "flex-start",
                  alignItems: "center",
                  width: "100%"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  {header.icon && <header.icon />}
                  {header.title}
                  {header.sortOrder && (
                    <StyledArrowIcon $isOpen={header.sortOrder === "desc"}>
                      <TableSortIcon />
                    </StyledArrowIcon>
                  )}
                </div>

                {isLastColumn && deletable && (
                  <StyledTableHeadItem
                    $isClickable={true}
                    $isDisabled={disableDeleteButton}
                    onClick={() => !disableDeleteButton}
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginLeft: "16px"
                    }}
                  >
                    <DeleteIcon />
                  </StyledTableHeadItem>
                )}
              </StyledTableHeadItem>
            </StyledTableHeaderContainer>
          );
        })}
      </tr>
    </StyledTableHead>
  );
};

const StyledTableHead = styled.thead`
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 2;
  background-color: ${COLOR_TABLE_TITLE};
`;

const StyledTableHeadItem = styled.div<{
  $isClickable: boolean;
  $isDisabled?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 700;
  color: #000000;
  ${({ $isDisabled }) => $isDisabled && `opacity: 0.3;`}
  ${({ $isClickable, $isDisabled }) =>
    $isClickable && `cursor: ${$isDisabled ? "not-allowed" : "pointer"};`}
  
`;

const rotate = ($isOpen: boolean | null) => keyframes`
  from {
    transform: rotate(${($isOpen === null ? 0 : $isOpen ? 0 : 180) + "deg"});
  }
  to {
    transform: rotate(${($isOpen === null ? 0 : $isOpen ? 180 : 0) + "deg"});
  }
`;

const StyledArrowIcon = styled.div<{ $isOpen: boolean | null }>`
  transform: ${({ $isOpen }) =>
    $isOpen === null
      ? "rotate(0deg)"
      : $isOpen
        ? "rotate(180deg)"
        : "rotate(0deg)"};
  animation: ${({ $isOpen }) => rotate($isOpen)} 0.2s forwards;
`;
const StyledTableHeaderContainer = styled.th<{ $width?: string }>`
  padding: 8px 16px;
  text-align: left;
  white-space: nowrap;
  position: relative;
  height: 40px;
  width: ${({ $width }) => $width || "1%"}; /* Apply custom width if provided */
  
  /* Flexible column sizing based on content */
  &:first-child {
    width: 1%; /* No column - minimal width */
    white-space: nowrap;
  }
  
  &:last-child {
    width: 1%; /* Actions column - minimal width */
    white-space: nowrap;
  }
`;

